import _ from 'underscore';
import { recommendationConfigs } from '../recommendation-configs';
import {
  RECOMMENDATION_TYPES,
} from '../consts';
import {
  formatEntityType,
  formatCost,
  formatPercent,
  formatBid,
  formatDecimal,
  truncateEntityName,
} from './basic-format-data';

const formatOutOf = (i18n, recommendationsCount, sampleCount = 1) => (recommendationsCount === 1 ? ''
  : `(${sampleCount} ${i18n.getString('Summary_Card_Out_Of')} ${recommendationsCount})`);

const formatActions = (type, actions, i18n, currency) => _.map(actions, (row) => {
  const fromString = i18n.getString('Summary_Card_Estimate_Primary_From');
  const toString = i18n.getString('Summary_Card_Estimate_Primary_To');
  switch (type) {
    case RECOMMENDATION_TYPES.BUDGET: {
      const budgetFrom = formatCost(row.budgetCurrent, i18n, currency);
      const budgetTo = formatCost(row.budgetCurrent + row.budgetIncrease, i18n, currency);
      return [
        `${fromString} ${budgetFrom} ${toString} ${budgetTo}`,
        `+${formatPercent(row.imprShareIncrease, i18n)}`,
      ];
    }
    case RECOMMENDATION_TYPES.COMPETITIVE_BID: {
      const bidFrom = formatBid(row.bidCurrent, i18n, currency);
      const bidTo = formatBid(row.bidSuggested, i18n, currency);
      return [
        row.keyword,
        `${fromString} ${bidFrom} ${toString} ${bidTo}`,
        `+${formatPercent(row.imprShareIncrease, i18n)}`,
      ];
    }
    case RECOMMENDATION_TYPES.LOCATION_BID_BOOST:
    case RECOMMENDATION_TYPES.DEVICE_BID_BOOST: {
      const bidBoostFrom = formatPercent(row.currentBidAdjustmentPercent / 100, i18n);
      const bidBoostTo = formatPercent(row.suggestedBidAdjustmentPercent / 100, i18n);
      return [
        type === RECOMMENDATION_TYPES.DEVICE_BID_BOOST ?
          row.device : row.location,
        `${fromString} ${bidBoostFrom} ${toString} ${bidBoostTo}`,
        `+${formatPercent(row.imprShareIncrease, i18n)}`,
      ];
    }
    case RECOMMENDATION_TYPES.NEW_LOCATION: {
      return [
        row.location,
        `+${formatDecimal(row.impressionsIncrease, i18n)}`,
      ];
    }
    default:
      return null;
  }
});

export const getModelForCompetitionSampleAction = ({
  type,
  i18n,
  recommendationsCount,
  level,
  entityName,
  currency,
  sample,
}) => {
  let actionTitle;
  let actions;

  // deprecated in Oct 2023
  if (type === RECOMMENDATION_TYPES.NEW_KEYWORD) {
    actionTitle = i18n.getString('Summary_Card_What_You_Can_Do_Generic');
    actions = i18n.getString(
      'Summary_Card_Action_New_Keyword',
      {
        n: recommendationsCount,
        entity_type: formatEntityType(level, recommendationsCount, i18n),
      }
    );
  } else if (type === RECOMMENDATION_TYPES.SYNDICATION) {
    actionTitle = i18n.getString('Summary_Card_What_You_Can_Do_Generic');
    actions = i18n.getString('Summary_Card_Action_Syndication');
  } else {
    actionTitle = i18n.getString(
      'Summary_Card_What_You_Can_Do',
      { entity_name: entityName }
    );
    const headers = _.map(recommendationConfigs[type].sampleActionHeaders, headerKey =>
      i18n.getString(headerKey));
    const highlights = _.map(recommendationConfigs[type].sampleActionHeaders, headerKey =>
      headerKey === 'Summary_Card_Action_Header_Est_Impressions'
      || headerKey === 'Summary_Card_Action_Header_Est_Impression_Share');
    actions = {
      headers,
      values: formatActions(type, sample.actions, i18n, currency),
      highlights,
    };
  }

  return {
    actions,
    actionTitle,
  };
};

export const getCompetitionSampleInsightTitle = ({
  type,
  i18n,
  entityName,
  recommendationsCount,
  sample,
}) => {
  let insightTitle = '';
  // what's going on
  if (type === RECOMMENDATION_TYPES.NEW_KEYWORD || type === RECOMMENDATION_TYPES.SYNDICATION) {
    insightTitle = i18n.getString(
      'Summary_Card_Whats_Going_Generic',
      { out_of: formatOutOf(i18n, recommendationsCount, sample.visualData.length) }
    );
  } else {
    insightTitle = i18n.getString(
      'Summary_Card_Whats_Going_On',
      {
        out_of: formatOutOf(i18n, recommendationsCount),
        entity_name: entityName,
      }
    );
  }
  return insightTitle;
};

export const formatCompetitionSampleVisualDataTable = ({
  type,
  visualData,
  visualTableHeaders,
  i18n,
  currency,
}) => {
  const headers = _.map(visualTableHeaders, headerKey => i18n.getString(headerKey));
  const formattedVisualData = { headers };
  switch (type) {
    case RECOMMENDATION_TYPES.NEW_KEYWORD: {
      formattedVisualData.highlights = [false, false, true];
      formattedVisualData.values = _.map(visualData, row => ([
        row.suggestedKeyword,
        /* row.monthlyQueryVolume,
        //bug 1457951: disable until est. weekly search volume is available */
        formatBid(row.bidSuggested, i18n, currency),
        `+${formatDecimal(row.impressionsIncrease, i18n)}`,
      ]));
      break;
    }
    case RECOMMENDATION_TYPES.SYNDICATION: {
      formattedVisualData.highlights = [false, false, false, true];
      formattedVisualData.alignments = ['left', 'left'];
      formattedVisualData.values = _.map(visualData, (row) => {
        const formattedAdoptionPercentage = formatPercent(row.adoptionPercentage / 100, i18n);
        const adoptionValue = {
          percentValue: row.adoptionPercentage,
          formattedValue: formattedAdoptionPercentage,
          useProgressBar: true,
        };
        return [
          truncateEntityName(row.campaignName),
          truncateEntityName(row.adGroupName),
          adoptionValue,
          `+${formatDecimal(row.impressionsIncrease, i18n)}`,
        ];
      });
      break;
    }
    default: {
      break;
    }
  }

  return formattedVisualData;
};
